import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBell, 
  faTimes, 
  faCheck, 
  faCheckDouble, 
  faUser, 
  faFile, 
  faShoppingCart, 
  faGraduationCap,
  faInfoCircle,
  faChevronRight,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import notificacaoService from '../services/notificacaoService';

// Mapeamento de ícones por tipo
const ICONES_TIPO = {
  // Notificações para operadores
  aluno_cadastrado: faUser,
  aluno_atualizado: faUser,
  documento_enviado: faFile,
  documento_atualizado: faFile,
  pedido_criado: faShoppingCart,
  pedido_atualizado: faShoppingCart,
  nota_lancada: faGraduationCap,
  nota_atualizada: faGraduationCap,
  // Notificações para coordenadores
  pedido_status_alterado: faShoppingCart,
  documento_aprovado: faFile,
  documento_rejeitado: faFile
};

// Mapeamento de cores por prioridade
const CORES_PRIORIDADE = {
  urgente: 'text-red-600 bg-red-100',
  alta: 'text-orange-600 bg-orange-100',
  normal: 'text-blue-600 bg-blue-100',
  baixa: 'text-gray-600 bg-gray-100'
};

/**
 * Componente de dropdown de notificações para o header
 */
const NotificacoesDropdown = ({ nivelAcesso, onClose }) => {
  const [notificacoes, setNotificacoes] = useState([]);
  const [totalNaoLidas, setTotalNaoLidas] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMarcar, setLoadingMarcar] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Verificar se o usuário pode ver notificações (operadores nível >= 4 OU coordenadores nível 2-3)
  const podeVerNotificacoes = nivelAcesso >= 2;

  const carregarNotificacoes = useCallback(async () => {
    if (!podeVerNotificacoes) return;

    try {
      setLoading(true);
      const response = await notificacaoService.obterResumo();
      if (response.success) {
        setNotificacoes(response.data.ultimas || []);
        setTotalNaoLidas(response.data.totalNaoLidas || 0);
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setLoading(false);
    }
  }, [podeVerNotificacoes]);

  useEffect(() => {
    carregarNotificacoes();
  }, [carregarNotificacoes]);

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleMarcarComoLida = async (e, notificacao) => {
    e.stopPropagation();
    try {
      await notificacaoService.marcarComoVisualizada(notificacao.id);
      await carregarNotificacoes();
    } catch (error) {
      console.error('Erro ao marcar notificação:', error);
    }
  };

  const handleMarcarTodasComoLidas = async () => {
    try {
      setLoadingMarcar(true);
      await notificacaoService.marcarTodasComoVisualizadas();
      await carregarNotificacoes();
    } catch (error) {
      console.error('Erro ao marcar todas as notificações:', error);
    } finally {
      setLoadingMarcar(false);
    }
  };

  const handleNotificacaoClick = async (notificacao) => {
    // Marcar como lida
    if (!notificacao.visualizada) {
      try {
        await notificacaoService.marcarComoVisualizada(notificacao.id);
      } catch (error) {
        console.error('Erro ao marcar notificação:', error);
      }
    }

    // Navegar para o link da notificação
    if (notificacao.link) {
      navigate(notificacao.link);
      onClose?.();
    }
  };

  const handleVerTodas = () => {
    navigate('/notificacoes');
    onClose?.();
  };

  const formatarData = (data) => {
    const dataObj = new Date(data);
    const agora = new Date();
    const diff = agora - dataObj;
    const minutos = Math.floor(diff / 60000);
    const horas = Math.floor(diff / 3600000);
    const dias = Math.floor(diff / 86400000);

    if (minutos < 1) return 'Agora mesmo';
    if (minutos < 60) return `${minutos} min atrás`;
    if (horas < 24) return `${horas}h atrás`;
    if (dias === 1) return 'Ontem';
    if (dias < 7) return `${dias} dias atrás`;
    return dataObj.toLocaleDateString('pt-BR');
  };

  if (!podeVerNotificacoes) {
    return null;
  }

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden"
    >
      {/* Cabeçalho */}
      <div className="bg-gradient-to-r from-green-400 to-blue-500 px-4 py-3 flex justify-between items-center">
        <div className="flex items-center space-x-2 text-white">
          <FontAwesomeIcon icon={faBell} />
          <span className="font-semibold">Notificações</span>
          {totalNaoLidas > 0 && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              {totalNaoLidas}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 transition"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* Ações do cabeçalho */}
      {totalNaoLidas > 0 && (
        <div className="px-4 py-2 border-b bg-gray-50 flex justify-between items-center">
          <span className="text-sm text-gray-600">
            {totalNaoLidas} não lida{totalNaoLidas > 1 ? 's' : ''}
          </span>
          <button
            onClick={handleMarcarTodasComoLidas}
            disabled={loadingMarcar}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
          >
            {loadingMarcar ? (
              <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
            ) : (
              <FontAwesomeIcon icon={faCheckDouble} className="mr-1" />
            )}
            <span>Marcar todas como lidas</span>
          </button>
        </div>
      )}

      {/* Lista de notificações */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <FontAwesomeIcon icon={faSpinner} spin className="text-gray-400 text-2xl" />
          </div>
        ) : notificacoes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FontAwesomeIcon icon={faBell} className="text-4xl mb-2 text-gray-300" />
            <p>Nenhuma notificação</p>
          </div>
        ) : (
          notificacoes.map((notificacao) => (
            <div
              key={notificacao.id}
              onClick={() => handleNotificacaoClick(notificacao)}
              className={`px-4 py-3 border-b hover:bg-gray-50 cursor-pointer transition ${
                !notificacao.visualizada ? 'bg-blue-50' : ''
              }`}
            >
              <div className="flex items-start space-x-3">
                {/* Ícone do tipo */}
                <div className={`p-2 rounded-full ${CORES_PRIORIDADE[notificacao.prioridade] || 'bg-gray-100 text-gray-600'}`}>
                  <FontAwesomeIcon 
                    icon={ICONES_TIPO[notificacao.tipo] || faInfoCircle} 
                    className="text-sm"
                  />
                </div>

                {/* Conteúdo */}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <p className={`text-sm font-medium ${!notificacao.visualizada ? 'text-gray-900' : 'text-gray-700'}`}>
                      {notificacao.titulo}
                    </p>
                    {!notificacao.visualizada && (
                      <button
                        onClick={(e) => handleMarcarComoLida(e, notificacao)}
                        className="ml-2 text-gray-400 hover:text-green-600 transition"
                        title="Marcar como lida"
                      >
                        <FontAwesomeIcon icon={faCheck} className="text-xs" />
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                    {notificacao.mensagem}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">
                      {formatarData(notificacao.created_at)}
                    </span>
                    {notificacao.criador_nome && (
                      <span className="text-xs text-gray-400">
                        por {notificacao.criador_nome}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Rodapé */}
      <div className="px-4 py-3 bg-gray-50 border-t">
        <button
          onClick={handleVerTodas}
          className="w-full text-center text-sm text-blue-600 hover:text-blue-800 flex items-center justify-center space-x-1"
        >
          <span>Ver todas as notificações</span>
          <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
        </button>
      </div>
    </div>
  );
};

/**
 * Botão de notificações para o header
 */
const NotificacoesButton = ({ nivelAcesso }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [totalNaoLidas, setTotalNaoLidas] = useState(0);
  const buttonRef = useRef(null);
  const lastFetchRef = useRef(0);
  // Usar ref para isLoading para não causar re-renders e loops infinitos
  const isLoadingRef = useRef(false);

  // Verificar se o usuário pode ver notificações (operadores nível >= 4 OU coordenadores nível 2-3)
  const podeVerNotificacoes = nivelAcesso >= 2;

  // Cache de 30 segundos para evitar chamadas excessivas
  const CACHE_TIME = 30000;
  // Intervalo de polling de 2 minutos (120 segundos) - reduzido para economizar requisições
  const POLLING_INTERVAL = 120000;
  // Ref para controlar backoff em caso de erro 429
  const backoffRef = useRef(1);

  const carregarContagem = useCallback(async (forceRefresh = false) => {
    if (!podeVerNotificacoes) return;

    // Verifica se já tem uma requisição em andamento ou se está no cache
    const now = Date.now();
    if (isLoadingRef.current || (!forceRefresh && (now - lastFetchRef.current) < CACHE_TIME)) {
      return;
    }

    try {
      isLoadingRef.current = true;
      const response = await notificacaoService.contarNaoLidas();
      if (response.success) {
        setTotalNaoLidas(response.data.total || 0);
        lastFetchRef.current = Date.now();
        // Reset backoff on success
        backoffRef.current = 1;
      }
    } catch (error) {
      // Em caso de erro 429 (Too Many Requests), aplicar backoff exponencial
      if (error?.response?.status === 429) {
        backoffRef.current = Math.min(backoffRef.current * 2, 8); // Max 8x backoff
        console.warn(`Rate limit atingido. Próxima tentativa em ${POLLING_INTERVAL * backoffRef.current / 1000}s`);
      } else if (error?.code !== 'ECONNABORTED') {
        // Silenciar erros de timeout para não poluir o console
        console.error('Erro ao carregar contagem de notificações:', error);
      }
    } finally {
      isLoadingRef.current = false;
    }
  }, [podeVerNotificacoes]);

  // Carregar na montagem inicial - executado apenas uma vez
  useEffect(() => {
    if (podeVerNotificacoes) {
      carregarContagem(true);
    }
  }, [podeVerNotificacoes, carregarContagem]);

  // Polling separado - executado apenas uma vez na montagem
  useEffect(() => {
    if (!podeVerNotificacoes) return;

    // Atualizar a cada 2 minutos
    const interval = setInterval(() => {
      carregarContagem(true);
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeVerNotificacoes]);

  // Recarregar quando o dropdown fechar
  const handleClose = () => {
    setIsOpen(false);
    // Pequeno delay para evitar chamada imediata após fechar
    setTimeout(() => carregarContagem(true), 500);
  };

  if (!podeVerNotificacoes) {
    return null;
  }

  return (
    <div className="relative" ref={buttonRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-1 sm:p-2 rounded-full bg-white text-gray-600 hover:bg-gray-200 transition duration-200"
        title="Notificações"
      >
        <FontAwesomeIcon icon={faBell} />
        {totalNaoLidas > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {totalNaoLidas > 99 ? '99+' : totalNaoLidas}
          </span>
        )}
      </button>

      {isOpen && (
        <NotificacoesDropdown
          nivelAcesso={nivelAcesso}
          onClose={handleClose}
        />
      )}
    </div>
  );
};

export { NotificacoesButton, NotificacoesDropdown };
export default NotificacoesButton;
